export type AuthUserType = 'candidate' | 'hiring_manager'

const RETURN_KEY = 'auth_return_path'

export function loginPathFor(userType: AuthUserType): string {
  return userType === 'candidate' ? '/app/candidate/login' : '/app/hiring-manager/login'
}

export function homePathFor(userType: AuthUserType): string {
  return userType === 'candidate' ? '/app/candidate/profile-hub' : '/app/hiring-manager/dashboard'
}

const CANDIDATE_PRACTICE_PATHS = ['/app/dashboard']

export function routeUserType(path: string): AuthUserType | null {
  if (path.startsWith('/app/candidate') || CANDIDATE_PRACTICE_PATHS.includes(path)) {
    return 'candidate'
  }
  if (path.startsWith('/app/w/') || path.startsWith('/app/hiring-manager')) {
    return 'hiring_manager'
  }
  return null
}

export function fallbackLoginForPath(pathname: string): string {
  if (pathname.startsWith('/admin')) {
    return '/admin/login'
  }
  if (pathname.startsWith('/app/candidate')) {
    return loginPathFor('candidate')
  }
  if (pathname.startsWith('/app/employee')) {
    return '/app/employee/login'
  }
  return loginPathFor('hiring_manager')
}

// The share link is the only return target outside the app: widening this
// pattern lets a crafted link send someone who just signed in to any path.
const SHARE_LINK_PATH = /^\/interview\/shared\/[A-Za-z0-9_-]{8,128}$/

export function shareLinkReturnPath(token: string): string | null {
  const path = `/interview/shared/${token}`
  return SHARE_LINK_PATH.test(path) ? path : null
}

function isSafeReturnPath(path: string): boolean {
  if (SHARE_LINK_PATH.test(path)) {
    return true
  }
  return (
    path.startsWith('/app') &&
    !path.startsWith('//') &&
    !path.includes('://') &&
    !path.includes('/login') &&
    !path.startsWith('/app/auth')
  )
}

export function storeReturnPath(path: string): void {
  if (isSafeReturnPath(path)) {
    sessionStorage.setItem(RETURN_KEY, path)
  } else {
    sessionStorage.removeItem(RETURN_KEY)
  }
}

export function consumeReturnPath(): string | null {
  const stored = sessionStorage.getItem(RETURN_KEY)
  sessionStorage.removeItem(RETURN_KEY)
  return stored && isSafeReturnPath(stored) ? stored : null
}

export function safeInternalPath(path: string | null | undefined): string | null {
  return path && isSafeReturnPath(path) ? path : null
}

export function returnPathFor(
  path: string | null | undefined,
  userType: AuthUserType
): string | null {
  const safe = safeInternalPath(path)
  if (!safe) {
    return null
  }
  const owner = SHARE_LINK_PATH.test(safe) ? 'candidate' : routeUserType(safe)
  return owner === null || owner === userType ? safe : null
}
